import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const PLUMAA_LOGO_URL =
	'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986601/Plumaa_H_psjyrs.png';

interface MatchShellProps {
	children: ReactNode;
	/** 0..1 — barra de progresso no topo. Omitir esconde a barra. */
	progress?: number;
	/** Texto ao lado da barra, ex.: "Pergunta 3 de 9". */
	stepLabel?: string;
	onBack?: () => void;
	/** Esconde o botão de fechar (ex.: durante o envio). */
	hideClose?: boolean;
	/** Área de conteúdo mais larga (tela de resultado com 3 cards). */
	wide?: boolean;
	className?: string;
}

/**
 * Moldura minimalista do /match: logo, progresso e conteúdo centralizado.
 * Inspirada nos onboardings de uma pergunta por tela (Preply).
 */
const MatchShell = ({
	children,
	progress,
	stepLabel,
	onBack,
	hideClose,
	wide = false,
	className,
}: MatchShellProps) => {
	const navigate = useNavigate();
	const showProgress = typeof progress === 'number';

	return (
		<div className="flex min-h-screen w-full flex-col bg-brand-cream text-stone-900">
			<header className="sticky top-0 z-20 border-b border-stone-200/60 bg-brand-cream/90 backdrop-blur">
				<div className="mx-auto flex h-16 w-full max-w-3xl items-center justify-between gap-4 px-4 md:px-6">
					<div className="flex items-center gap-2">
						{onBack ? (
							<button
								type="button"
								onClick={onBack}
								aria-label="Voltar"
								className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 transition-colors hover:border-stone-900 hover:text-stone-900"
							>
								<ArrowLeft className="h-4 w-4" />
							</button>
						) : null}
						<button
							type="button"
							onClick={() => navigate('/')}
							className="flex items-center"
							aria-label="Ir para a página inicial"
						>
							<img
								src={PLUMAA_LOGO_URL}
								alt="Plumaa"
								className="h-7 w-auto object-contain md:h-8"
							/>
						</button>
					</div>

					{stepLabel ? (
						<span className="hidden text-[11px] font-black uppercase tracking-[0.18em] text-stone-400 sm:block">
							{stepLabel}
						</span>
					) : null}

					{!hideClose ? (
						<button
							type="button"
							onClick={() => navigate('/')}
							aria-label="Sair do match"
							className="flex h-9 w-9 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-200/60 hover:text-stone-900"
						>
							<X className="h-4 w-4" />
						</button>
					) : (
						<span className="h-9 w-9" />
					)}
				</div>

				{showProgress ? (
					<div className="h-1 w-full bg-stone-200/70">
						<motion.div
							className="h-full rounded-r-full bg-brand-pink"
							initial={false}
							animate={{
								width: `${Math.max(
									2,
									Math.min(100, progress * 100),
								)}%`,
							}}
							transition={{
								type: 'spring',
								stiffness: 120,
								damping: 20,
							}}
						/>
					</div>
				) : null}
			</header>

			{stepLabel ? (
				<p className="mx-auto mt-4 w-full max-w-3xl px-4 text-[11px] font-black uppercase tracking-[0.18em] text-stone-400 sm:hidden md:px-6">
					{stepLabel}
				</p>
			) : null}

			<main
				className={cn(
					'mx-auto flex w-full flex-1 flex-col px-4 pb-16 pt-8 md:px-6 md:pt-12',
					wide ? 'max-w-5xl' : 'max-w-3xl',
					className,
				)}
			>
				{children}
			</main>
		</div>
	);
};

export { MatchShell };
